import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminTeamsPage.css";
import { useI18n } from "../providers/I18nProvider";
import { canManageOrganization } from "../utils/roles";
import PaginationControls from "../components/PaginationControls";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../providers/ConfirmProvider";
import NoirAlert from "../components/NoirAlert";
import { TabBar, FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";

const AdminTeamsPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [managerUserId, setManagerUserId] = useState("");

  const canManageTeams = canManageOrganization(me);

  const pageSizeKey = "pageSize.adminTeams";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const [tab, setTab] = useState("all"); // all | withManager | noManager
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const notifyError = (err) =>
    setNotification({
      message: err?.response?.data?.msg || err?.message || t("common.genericError"),
      type: "error",
    });

  const memberCounts = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      if (u.team_name) map[u.team_name] = (map[u.team_name] || 0) + 1;
    });
    return map;
  }, [users]);

  const tabCounts = useMemo(
    () => ({
      all: teams.length,
      withManager: teams.filter((tm) => tm.manager_name).length,
      noManager: teams.filter((tm) => !tm.manager_name).length,
    }),
    [teams]
  );

  const filteredTeams = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return teams
      .filter((tm) => {
        if (tab === "withManager" && !tm.manager_name) return false;
        if (tab === "noManager" && tm.manager_name) return false;
        if (!q) return true;
        return `${tm.name || ""} ${tm.manager_name || ""} ${tm.manager_surname || ""} ${
          tm.manager_username || ""
        }`
          .toLocaleLowerCase("tr-TR")
          .includes(q);
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"));
  }, [teams, tab, search]);

  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredTeams.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredTeams.length, page, pageSize]);

  const pagedTeams = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTeams.slice(start, start + pageSize);
  }, [filteredTeams, page, pageSize]);

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [teamsRes, usersRes] = await Promise.all([
        apiClient.get("/teams"),
        apiClient.get("/users"),
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTeam = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim()) {
      setNotification({ message: t("adminTeams.nameRequired"), type: "error" });
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/teams", {
        name: name.trim(),
        manager_user_id: managerUserId ? Number(managerUserId) : null,
      });
      setName("");
      setManagerUserId("");
      await fetchAll();
      setNotification({ message: t("common.saved"), type: "success" });
    } catch (err) {
      notifyError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTeam = async (id) => {
    if (!(await confirm(t("admin.teams.deleteConfirm")))) return;
    try {
      await apiClient.delete(`/teams/${id}`);
      await fetchAll();
    } catch (err) {
      notifyError(err);
    }
  };

  if (loading) return <div className="admin-teams">{t("common.loading")}</div>;
  if (error) return <div className="admin-teams error">{t("common.error")}: {error}</div>;

  return (
    <div className="admin-teams">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="admin-teams-header">
        <h2>{t("admin.teams.title")}</h2>
      </div>

      {canManageTeams && (
        <form className="team-form" onSubmit={createTeam}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("admin.teams.namePlaceholder")}
            required
          />
          <select value={managerUserId} onChange={(e) => setManagerUserId(e.target.value)}>
            <option value="">{t("admin.teams.managerPlaceholder")}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} {u.surname} ({u.username})
              </option>
            ))}
          </select>
          <button className="primary" type="submit" disabled={submitting}>
            {t("admin.teams.add")}
          </button>
        </form>
      )}

      <TabBar
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "all", label: t("adminTeams.tabs.all"), count: tabCounts.all },
          { key: "withManager", label: t("adminTeams.tabs.withManager"), count: tabCounts.withManager },
          { key: "noManager", label: t("adminTeams.tabs.noManager"), count: tabCounts.noManager, attention: true },
        ]}
      />
      <FilterBar showReset={!!search} onReset={() => setSearch("")}>
        <SearchInput value={search} onChange={setSearch} placeholder={t("adminTeams.search")} />
      </FilterBar>
      {filteredTeams.length === 0 && <EmptyState message={t("adminTeams.emptyFiltered")} />}

      <div className="table-wrap">
        <PaginationControls
          totalItems={filteredTeams.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        <table className="teams-table">
          <thead>
            <tr>
              <th>{t("admin.teams.columns.name")}</th>
              <th>{t("admin.teams.columns.manager")}</th>
              <th>{t("adminTeams.members")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pagedTeams.map((team) => (
              <tr key={team.id}>
                <td>{team.name}</td>
                <td>
                  {team.manager_name
                    ? `${team.manager_name} ${team.manager_surname} (${team.manager_username})`
                    : "-"}
                </td>
                <td className="mono">{memberCounts[team.name] || 0}</td>
                <td>
                  <div className="actions">
                    {canManageTeams ? (
                      <button className="danger" onClick={() => deleteTeam(team.id)}>
                        {t("common.delete")}
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls
          totalItems={filteredTeams.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
};

export default AdminTeamsPage;
